import { cn } from '@hrms/ui';
import { useLeaveSummary } from '../leave/hooks.js';
import { HomeCard } from './HomeCard.js';

/** At or below this share of the yearly quota left, the bar turns to a warning colour. */
const LOW_BALANCE_SHARE = 0.2;

/**
 * Days left for the caller's current leave year: the total as a headline, then one bar per leave
 * type showing what remains of its quota. Straight from `GET /leave/summary` — no placeholder
 * types, so an employee with no leave types sees an empty state rather than someone else's numbers.
 */
export function LeaveBalanceCard() {
  const query = useLeaveSummary();
  const summary = query.data?.status === 200 ? query.data.body : undefined;
  const types = summary?.perType ?? [];

  return (
    <HomeCard
      title="Leave Balance"
      viewAll={{ to: '/leave', label: 'View details' }}
      query={query}
      isEmpty={summary !== undefined && types.length === 0}
      emptyTitle="No leave types assigned"
      emptyDescription="Your leave balance will show here once a policy is assigned."
    >
      {summary && types.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline gap-1.5">
            <span className="font-display text-3xl font-extrabold tabular-nums text-fg">
              {summary.availableTotalDays}
            </span>
            <span className="text-xs font-medium text-fg-muted">days left this year</span>
          </div>

          <ul className="flex flex-col gap-3">
            {types.map((type) => {
              const left = Number(type.balanceDays);
              const quota = Number(type.quotaDays);
              const share = quota > 0 ? Math.min(1, Math.max(0, left / quota)) : 0;
              return (
                <li key={type.leaveTypeId} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="min-w-0 truncate font-medium text-fg">
                      {type.leaveTypeName}
                    </span>
                    <span className="shrink-0 tabular-nums text-fg-muted">
                      <span className="font-semibold text-fg">{type.balanceDays}</span>
                      {' / '}
                      {type.quotaDays} days
                    </span>
                  </div>
                  <div
                    role="progressbar"
                    aria-label={`${type.leaveTypeName}: ${type.balanceDays} of ${type.quotaDays} days left`}
                    aria-valuemin={0}
                    aria-valuemax={quota}
                    aria-valuenow={left}
                    className="h-1.5 overflow-hidden rounded-full bg-bg-subtle"
                  >
                    <div
                      className={cn(
                        'h-full rounded-full transition-[width]',
                        quota > 0 && share <= LOW_BALANCE_SHARE ? 'bg-warning' : 'bg-accent',
                      )}
                      style={{ width: `${share * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </HomeCard>
  );
}
